import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { isEmpty } from 'lodash-es';
import { Dropdown as RapydDropdown } from '@rapyd/react-ui-library';
import {
  DropdownComponentProps,
  Option
} from '@rapyd/react-ui-library/dist/components/Form/Dropdown';
import { getThemeTypography, ThemeBreakpoints, Typographies } from 'models';
import { getClearCustomStyles } from 'components/core/styles';
import { boxRoundness } from 'styles/global';

export type OptionValue = Option['value'];

export interface IDropdownProps extends Omit<DropdownComponentProps, 'onChange'> {
  value?: OptionValue;
  defaultValue?: OptionValue;
  onChange: (value: OptionValue) => void;
  disableOneOption?: boolean;
  oneOptionStyleDropdownAsDiv?: boolean;
}

const calcVal = (value: OptionValue | undefined, options: Option[]): Option | undefined =>
  options?.find(option => option?.value === value);

const Dropdown = ({
  className,
  name,
  value,
  options,
  defaultValue,
  onChange,
  fieldBoxProps,
  disabled,
  isClearable = true,
  disableOneOption = true,
  oneOptionStyleDropdownAsDiv,
  ...rest
}: IDropdownProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleBlur = (): void => {
    setIsFocused(false);
  };

  const handleFocus = (): void => {
    setIsFocused(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  const handleChange = (newValue: any, _meta: any): void => {
    if (onChange) {
      onChange(newValue?.value);
    }
  };

  const parsedValue = calcVal(value, options);
  const parsedDefaultValue = calcVal(defaultValue, options);
  const dropdownValue = parsedValue || parsedDefaultValue;

  const fieldName = name || 'dropdown';

  const isClearableValue = isClearable && !disabled && options?.length > 1;
  const oneOptionDisabledValue = disableOneOption && options?.length === 1;
  const isOneOptionStyleDropdownAsDiv =
    oneOptionStyleDropdownAsDiv && options?.length === 1;

  return (
    <StyledRapydDropdown
      className={className}
      name={fieldName}
      value={dropdownValue}
      options={options}
      onChange={handleChange}
      disabled={disabled || oneOptionDisabledValue}
      $oneOptionDisabled={oneOptionDisabledValue}
      $styleDropdownAsDiv={isOneOptionStyleDropdownAsDiv}
      $isActive={isFocused}
      fieldBoxProps={{
        ...fieldBoxProps,
        name: fieldName,
        staticSize: ThemeBreakpoints.LARGE,
        onBlur: handleBlur,
        onFocus: handleFocus,
        isClearable: isClearableValue
      }}
      {...rest}
    />
  );
};

export default Dropdown;

const getCustomStyles =
  () =>
  (
    dropdownProps: DropdownComponentProps & { theme: Record<string, any> } & {
      $isActive: boolean;
      $oneOptionDisabled: boolean;
    }
  ) => {
    const { name, theme, $isActive, disabled, fieldBoxProps } = dropdownProps || {};
    const isError = !isEmpty(fieldBoxProps?.error);

    const baseObject = theme?.hcp?.dropdown;
    if (isEmpty(baseObject)) return;

    return css`
      fieldset {
        font-size: ${baseObject?.base?.fontSize};
        font-weight: ${baseObject?.base?.fontWeight};
        font-family: ${baseObject?.base?.fontFamily};
        border-color: ${baseObject?.base?.borderColor};
        background-color: ${baseObject?.base?.backgroundColor};
        border-radius: ${baseObject?.base?.borderRadius};
      }

      label[data-testid='${name}-label'] {
        color: ${baseObject?.base?.labelColor};
      }

      .dropdown__single-value {
        color: ${baseObject?.base?.color};
      }

      .dropdown__placeholder {
        font-size: ${baseObject?.placeholder?.fontSize};
        font-weight: ${baseObject?.placeholder?.fontWeight};
        font-family: ${baseObject?.placeholder?.fontFamily};
        color: ${baseObject?.placeholder?.color};
      }

      .dropdown__option:not(.dropdown__option--is-selected) {
        background-color: ${baseObject?.item?.base?.backgroundColor};
        color: ${baseObject?.item?.base?.color};
        font-family: ${baseObject?.item?.base?.fontFamily};
        transition: ${baseObject?.item?.transition};

        &.dropdown__option--is-focused {
          background-color: ${baseObject?.item?.hover?.backgroundColor};
          color: ${baseObject?.item?.hover?.color};
          font-family: ${baseObject?.item?.hover?.fontFamily};
        }
      }

      .dropdown__option.dropdown__option--is-selected {
        background-color: ${baseObject?.item?.selected?.backgroundColor};
        color: ${baseObject?.item?.selected?.color};
        font-family: ${baseObject?.item?.selected?.fontFamily};
      }

      .dropdown__menu-list {
        background-color: ${baseObject?.list?.backgroundColor};
        border: ${baseObject?.list?.border};
        border-radius: ${baseObject?.list?.borderRadius};
        box-shadow: ${baseObject?.list?.boxShadow};
      }

      ${$isActive &&
      `
      > div[data-testid='${name}-form-element-wrapper'] {
        outline: ${baseObject?.focus?.outline};
      }

      fieldSet {
        border-color: ${baseObject?.active?.borderColor};
        background-color: ${baseObject?.active?.backgroundColor};
      }

      label[data-testid='${name}-label'] {
        color: ${baseObject?.active?.labelColor};
      }

      .dropdown__single-value {
        color: ${baseObject?.active?.color};
      }
    `};

      ${isError &&
      `
      fieldSet {
        border-color: ${baseObject?.error?.borderColor};
        background-color: ${baseObject?.error?.backgroundColor};
      }

      label[data-testid='${name}-label'] {
        color: ${baseObject?.active?.labelColor};
      }

      .dropdown__single-value {
        color: ${baseObject?.error?.color};
      }
    `};

      ${disabled &&
      `
      fieldSet {
        background-color: ${baseObject?.disabled?.backgroundColor};
      }

      .dropdown__single-value {
        color: ${baseObject?.disabled?.color};
      }
    `};
    `;
  };

const StyledRapydDropdown = styled(RapydDropdown)<{
  $oneOptionDisabled: boolean;
  $isActive: boolean;
  $styleDropdownAsDiv?: boolean;
}>`
  .dropdown__single-value,
  .dropdown__option {
    text-transform: none;
  }

  .dropdown__option {
    margin: 0.7rem 0;
  }

  .dropdown__single-value > div,
  .dropdown__option > div {
    align-items: center;
    font-size: ${({ theme }) => theme.view.isMobile && theme.fonts.size.medium};
  }

  ${({ $isActive, value }) =>
    ($isActive || !!value) &&
    css`
      label,
      legend span {
        font-size: ${({ theme }) => theme.fonts.size.extraSmall};
      }
    `}

  ${({ $oneOptionDisabled }) =>
    $oneOptionDisabled &&
    css`
      .dropdown__indicators {
        display: none;
      }
    `}

  ${getCustomStyles()};
  ${getClearCustomStyles()};

  ${({ $styleDropdownAsDiv }) =>
    $styleDropdownAsDiv &&
    css`
      box-shadow: 0 0 2.4rem 0 rgba(0, 0, 0, 0.04);
      * {
        background-color: ${({ theme }) => theme.colors.white};
        ${getThemeTypography(Typographies.BODY1)};
        color: ${({ theme }) => theme.colors.surface1};
        border: none;
        -webkit-font-smoothing: antialiased;
        ${boxRoundness};

        .dropdown__single-value {
          color: ${({ theme }) => theme.colors.surface1};
        }
        legend {
          display: none;
        }
      }
    `};
`;
